import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IApiUserAuthenticated } from '../../interfaces/IApiUserAuthenticated';
import { IApiUserToken } from '../../interfaces/IApiUserToken';
import { SegTusuarios, UserChangePassword, UserCreation, UserCredentials, UserModification, UserRegistration, ValidateUser, AccountResponse } from '../../models/user-credentials';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WebApiBaseService } from '../../web-api-base/web-api-base.service';
import { IEmailReset, IPassReset } from '../../models/passReset.model';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends WebApiBaseService {
  private userLocalStorage: string = 'currentUser';
  private rememberLocalStorage: string = 'remember';
  private timeoutRefreshToken: any;
  private sessionValidationInterval: any;

  public currentUser: BehaviorSubject<IApiUserAuthenticated | null>;

  constructor(private http: HttpClient, private route: Router, private cookie: CookieService) {
    super();
    this.currentUser = new BehaviorSubject
      (
        JSON.parse(localStorage.getItem(this.userLocalStorage) || "null")
      );

    if (this.getUser != null && JSON.parse(localStorage.getItem(this.rememberLocalStorage) || "{}")) {
      this.setRefreshToken();
    }

    // Iniciar validación periódica de sesión
    this.startSessionValidation();
  }

  get getUser(): IApiUserAuthenticated | null {
    return this.currentUser.value;
  }

  get loggedIn(): boolean {
    const user = this.currentUser.value;
    if (!user) return false;

    // Validar que el token no esté vencido
    return this.isTokenValid(user.JwtToken);
  }

  /**
   * Verifica si el token JWT es válido y no ha vencido
   */
  isTokenValid(token: string): boolean {
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convertir a milisegundos
      const now = Date.now();

      const isValid = exp > now;

      if (!isValid) {
        console.log('🚨 Token vencido detectado:', {
          expiry: new Date(exp),
          now: new Date(now),
          expired: exp <= now
        });
      }

      return isValid;
    } catch (error) {
      console.error('❌ Error al validar token:', error);
      return false;
    }
  }

  /**
   * Verifica el estado de la sesión y limpia si está corrupta
   */
  validateSessionState(): boolean {
    const user = this.getUser;

    if (!user) {
      console.log('✅ No hay usuario - estado normal');
      return true;
    }

    // Verificar si el token es válido
    if (!this.isTokenValid(user.JwtToken)) {
      console.log('🚨 Token vencido detectado - limpiando sesión');
      this.clearCorruptedSession('Token vencido');
      return false;
    }

    // Verificar inconsistencias de impersonalización
    const originalUser = localStorage.getItem('originalUser');
    const impersonatedData = localStorage.getItem('impersonatedUserData');

    if (originalUser && !impersonatedData) {
      console.log('🚨 Estado de impersonalización inconsistente - limpiando');
      this.clearCorruptedSession('Estado de impersonalización inconsistente');
      return false;
    }

    console.log('✅ Estado de sesión válido');
    return true;
  }

  /**
   * Limpia una sesión corrupta de manera segura
   */
  clearCorruptedSession(reason: string): void {
    console.log(`🧹 Limpiando sesión corrupta: ${reason}`);

    try {
      // Limpiar datos de usuario actual
      this.currentUser.next(null);
      this.CleanUserLocalStorage();
      this.CleanRememberLocalStorage();

      // Limpiar datos de impersonalización
      this.cleanImpersonationDataFromAuth();

      // Limpiar cookies
      if (this.cookie.check('RefreshToken')) {
        this.cookie.delete('RefreshToken');
      }

      // Cancelar timeout de refresh token
      if (this.timeoutRefreshToken != null) {
        clearTimeout(this.timeoutRefreshToken);
        this.timeoutRefreshToken = null;
      }

      // Detener validación de sesión
      this.stopSessionValidation();

      console.log('✅ Sesión corrupta limpiada exitosamente');
      this.route.navigateByUrl('login-form');
    } catch (error) {
      console.error('❌ Error al limpiar sesión corrupta:', error);

      // Limpieza de emergencia
      const criticalKeys = ['currentUser', 'remember', 'originalUser', 'impersonatedUserData'];
      criticalKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      });
      this.route.navigateByUrl('login-form');
    }
  }

  /**
   * Inicia la validación periódica del estado de sesión
   */
  private startSessionValidation(): void {
    // Validar cada 2 minutos
    this.sessionValidationInterval = setInterval(() => {
      if (this.getUser) {
        const isValid = this.validateSessionState();
        if (!isValid) {
          console.log('🚨 Validación periódica: Sesión inválida detectada');
          this.stopSessionValidation();
        }
      }
    }, 2 * 60 * 1000); // 2 minutos
  }

  /**
   * Detiene la validación periódica del estado de sesión
   */
  private stopSessionValidation(): void {
    if (this.sessionValidationInterval) {
      clearInterval(this.sessionValidationInterval);
      this.sessionValidationInterval = null;
    }
  }

  /**
   * Método de limpieza para logout
   */
  createHeaders(responseType?: string): HttpHeaders {

    const currentUser = this.getUser;
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Response-Type': responseType || 'application/json'
    });

    if (currentUser && currentUser.JwtToken) {
      headers = headers.set('Authorization', `Bearer ${currentUser.JwtToken}`);
    }

    return headers;
  }
  get rememberSession(): boolean {
    return JSON.parse(localStorage.getItem(this.rememberLocalStorage) || "{}");
  }

  login(user: UserCredentials, remember: boolean): Observable<IApiUserAuthenticated> {

    return this.http.post<IApiUserAuthenticated>(`${this.baseUrl}Accounts/Authenticate`, user)
      .pipe(
        map(r => {
          if (remember) {
            this.setUserLocalStorage(r);
            this.setRememberLocalStorage(remember);
          }
          else {
            this.CleanUserLocalStorage();
            this.CleanRememberLocalStorage();
          }

          this.currentUser.next(r);

          // Redirección basada en rol
          switch (r.Role) {
            case "Admin":
              this.route.navigateByUrl('inicio');
              break;
            case "Repartidor":
              this.route.navigateByUrl('repartidor/mis-envios');
              break;
            case "User":
              this.route.navigateByUrl('PedidosMayorista');
              break;
            default:
              this.route.navigateByUrl('PedidosMayorista');
          }

          this.setRefreshToken();
          return r;
        })
      );
  }

  // refreshToken(): void {

  //  let user = this.getUser;
  //  let a=user!.RefreshToken;
  //  this.cookie.set('refreshToken',a)
  //  const refreshToken:string = a ;

  //  const headers = new HttpHeaders({
  //   'refreshToken': refreshToken,
  //   'accept': 'text/plain',
  //   'Content-Type': 'application/json-patch+json'
  // });

  // const options = {
  //   headers: headers
  // };

  // this.http.post<any>(`${this.baseUrl}Accounts/refresh-token`, null, options)
  //   .subscribe(
  //     (r:any) => {
  //          this.setUserLocalStorage(r);
  //          this.currentUser.next(r);

  //          this.setRefreshToken();
  //         return r;
  //     }
  //   )

  // }

  refreshToken(): void {
    let user = this.getUser;
    let a = user!.RefreshToken;
    this.cookie.set('RefreshToken', a)
    const RefreshToken: string = a;

    const headers = new HttpHeaders({
      'refreshToken': RefreshToken,
      'accept': 'text/plain',
      'Content-Type': 'application/json-patch+json'
    });

    const options = {
      headers: headers
    };

    this.http.post<any>(`${this.baseUrl}Accounts/refresh-token`, null, options)
      .subscribe({
        next: (response: any) => {
          //  console.log('Respuesta completa del refresh:', response);
          //  console.log('Refresh token recibido:', response?.RefreshToken);
          //  console.log('Estado del usuario:', {
          //    token: response?.JwtToken,
          //    RefreshToken: response?.RefreshToken,
          //    role: response?.Role,
          //    // otros campos relevantes...
          //  });

          if (!response || !response.RefreshToken) {
            console.error('No se recibió un refresh token válido');
            this.handleRefreshError('No se recibió un refresh token válido');
            return;
          }

          this.setUserLocalStorage(response);
          this.currentUser.next(response);
          this.setRefreshToken();
        },
        error: (error) => {
          console.error('Error detallado:', {
            status: error.status,
            message: error.message,
            error: error.error,
            headers: error.headers?.keys()
          });
          this.handleRefreshError(error);
        }
      });
  }
  private handleRefreshError(error: any): void {
    console.log('🚨 Error en refresh token:', error);

    // Verificar si está impersonalizado
    const originalUser = localStorage.getItem('originalUser');
    const isImpersonating = !!originalUser;

    if (isImpersonating && originalUser) {
      console.log('🔄 Impersonalización detectada durante error de refresh - intentando restaurar usuario original');

      try {
        const originalUserData = JSON.parse(originalUser);

        // Verificar si el usuario original tiene un token válido
        if (originalUserData && this.isTokenValid(originalUserData.JwtToken)) {
          console.log('✅ Restaurando usuario original con token válido');

          // Limpiar datos de impersonalización
          this.cleanImpersonationDataFromAuth();

          // Restaurar usuario original
          this.setUserLocalStorage(originalUserData);
          this.currentUser.next(originalUserData);

          // Configurar nuevo refresh token para el usuario original
          this.setRefreshToken();

          console.log('✅ Usuario original restaurado exitosamente');
          return;
        } else {
          console.log('❌ Token del usuario original también vencido');
        }
      } catch (parseError) {
        console.error('❌ Error al parsear usuario original:', parseError);
      }
    }

    // Si no está impersonalizado o la restauración falló, limpiar todo
    console.log('🧹 Limpiando sesión completamente');
    this.clearCorruptedSession('Error en refresh token');

    // Redirigir al login
    this.route.navigate(['/login-form']);
  }
  setUserLocalStorage(user: IApiUserAuthenticated): void {
    localStorage.setItem(this.userLocalStorage, JSON.stringify(user));
  }

  setRememberLocalStorage(remember: boolean): void {
    localStorage.setItem(this.rememberLocalStorage, JSON.stringify(remember));
  }

  CleanUserLocalStorage(): void {
    localStorage.removeItem(this.userLocalStorage);
  }

  CleanRememberLocalStorage(): void {
    localStorage.removeItem(this.rememberLocalStorage);
  }

  /**
   * Limpia específicamente los datos de impersonación del localStorage
   */
  cleanImpersonationDataFromAuth(): void {
    console.log('AuthService: Limpiando datos de impersonación...');

    // Claves específicas de impersonación que deben ser eliminadas
    const impersonationKeys = [
      'originalUser',
      'impersonatedUserData',
      'impersonatedUser'
    ];

    impersonationKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`AuthService: Eliminada clave ${key}`);
      }
    });
  }

  logout(): void {
    localStorage.removeItem(this.userLocalStorage);
    this.currentUser.next(null);

    // Limpiar timeouts y intervalos
    if (this.timeoutRefreshToken != null) {
      clearTimeout(this.timeoutRefreshToken);
      this.timeoutRefreshToken = null;
    }

    this.stopSessionValidation();

    // Limpiar datos de impersonalización si existen
    this.cleanImpersonationDataFromAuth();

    this.route.navigateByUrl('login-form');
  }

  setRefreshToken(): void {


    const min: number = 1;
    const max: number = 15;
    const intervalo = Math.floor(Math.random() * (max - min + 1)) + min;

    this.timeoutRefreshToken = setTimeout(() => {
      this.refreshToken();
    }, intervalo * 1000 * 60);
  }


  resetPassword(data: IEmailReset) {
    return this.http.post<IPassReset>(`${this.baseUrl}Accounts/forgot-password`, data);

  }

  crearCuenta(userCreated: UserRegistration) {

    return this.http.post<any>(`${this.baseUrl}Accounts/register`, userCreated);
    //return this.http.post<IApiUserAuthenticated>(`${this.baseUrl}security/add`, user);

  }

  modificarCuenta(usuario: UserModification) {
    return this.http.put<AccountResponse>(`${this.baseUrl}Accounts/${usuario.id}`, usuario);
  }


  changePassword(changePassDto: UserChangePassword) {
    return this.http.post<IPassReset>(`${this.baseUrl}Accounts/reset-password`, changePassDto);

  }

  validateUserEmail(userToken: ValidateUser) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json-patch+json',
      'Accept': '*/*',
    });

    const body = {
      Token: userToken.Token,
    };
    return this.http.post<any>(`${this.baseUrl}Accounts/verify-email`, body, { headers });
  }


}
